import React, {useState, useEffect, useCallback} from "react";
import {Button, message, Modal, Radio, Space} from "antd";
import {CreditCardOutlined, WalletOutlined, TruckOutlined} from "@ant-design/icons";
import {useNavigate, useLocation} from "react-router-dom";
import {Helmet} from "react-helmet";
import CartItem from "./CartItem";
import {language} from "../../language/Language";
import {useCart} from "../../Context/CartContext";
import Loader2 from "../../Components/Loader2";
import {useCreateOrderFromCartMutation, useGetGovernorateQuery, useGetOneProductQuery, useGetWorkHoursQuery} from "../../RTK/Slices/Slices/apiSliceMeat";
import {
    getCart,
    getCartTotal,
    getCartItemsCount,
    clearCart,
    removeFromCart,
    validateOrderData,
    prepareOrderData,
    calculateDiscountedPrice,
    saveCart,
} from "../../utils/localStorage";

// ============================================================
// مكون يجيب سعر منتج واحد من الـ API ويحدث السلة فوراً
// ============================================================
const CartItemSyncer = ({ productId, count, onSynced }) => {
    const { data, isSuccess } = useGetOneProductQuery(productId, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (isSuccess && data?.data?.[0]) {
            const latest = data.data[0];
            const newDiscount = parseFloat(latest.default_sales_discount || 0);
            const newOriginalPrice = parseFloat(latest.price);
            const newUnitPrice = parseFloat(calculateDiscountedPrice(latest.price, newDiscount));

            const cart = getCart();
            const idx = cart.findIndex(i => i.product_id === productId);
            if (idx === -1) return;

            const oldUnitPrice = parseFloat(cart[idx].unit_price);
            if (Math.abs(newUnitPrice - oldUnitPrice) > 0.01 ||
                Math.abs(newOriginalPrice - parseFloat(cart[idx].original_price || cart[idx].unit_price)) > 0.01) {
                cart[idx] = {
                    ...cart[idx],
                    unit_price: newUnitPrice.toFixed(2),
                    price: (newUnitPrice * cart[idx].count).toFixed(2),
                    original_price: newOriginalPrice.toFixed(2),
                    discount: newDiscount,
                    stock: latest.stock,
                };
                saveCart(cart);
                onSynced(); // أخبر الـ Cart إنه يعيد القراءة من localStorage
            }
        }
    }, [isSuccess, data, productId, onSynced]);

    return null;
};


const texts = {
    ar: {
        deliveryTo: "سعر التوصيل إلى",
        deliveryTime: "وقت التوصيل:",
        hours: "ساعة",
        EGP: "جنيه",
    },
    en: {
        deliveryTo: "Delivery price to",
        deliveryTime: "Delivery time:",
        hours: "Hour",
        EGP: "EGP ",
    },
};
const t = texts[language === "en" ? "en" : "ar"];

const CartList = () => {
    // State management
    const [cartItems, setCartItems] = useState([]);
    const [cartTotal, setCartTotal] = useState("0.00");
    const [cartItemsCount, setCartItemsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
    const [outOfStockItems, setOutOfStockItems] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("cod");

    // localStorage
    const userData = localStorage.getItem("user");
    const jsonUserData = userData ? JSON.parse(userData) : null;

    // Hooks
    const {updateCartCount} = useCart();
    const [createOrder, {isLoading: isCreatingOrder}] = useCreateOrderFromCartMutation();
    const { data: workHoursData } = useGetWorkHoursQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // قراءة بيانات المستخدم من localStorage
    const userGovernorate = jsonUserData?.governorate?.name ?? null;
    console.log("userGovernorate", userGovernorate);

    // جلب بيانات المحافظات
    const {
        data: governorateData,
    } = useGetGovernorateQuery();



    console.log("governorateData", governorateData?.data);

    // استخراج سعر المحافظة
    // const deliveryPrice = governorateData?.data?.find((gov) => gov.name === userGovernorate)?.price;
    const deliveryPrice = governorateData?.data?.find((gov) => gov.name === userGovernorate)?.price || 0;

    console.log("deliveryPrice", deliveryPrice);

    // إنشاء العنوان الكامل من بيانات المستخدم بالترتيب الصحيح
    const getFullAddress = useCallback(() => {
        if (!jsonUserData) return "";

        const parts = [];

        // المحافظة / منطقة التوصيل
        if (jsonUserData.governorate?.name) {
            parts.push(jsonUserData.governorate.name);
        }

        // اسم الشارع (address_line_1 في الـ DB — حقل address في الـ user)
        if (jsonUserData.address) {
            parts.push(`شارع ${jsonUserData.address}`);
        }

        // رقم المبنى (مخزّن في حقل city)
        if (jsonUserData.city) {
            parts.push(`عمارة ${jsonUserData.city}`);
        }

        // رقم الدور (مخزّن في حقل street)
        if (jsonUserData.street) {
            parts.push(`دور ${jsonUserData.street}`);
        }

        // رقم الشقة (مخزّن في حقل building)
        if (jsonUserData.building) {
            parts.push(`شقة ${jsonUserData.building}`);
        }

        return parts.join(" - ");
    }, [jsonUserData]);

    // إعداد بيانات الشحن الحقيقية من بيانات المستخدم
    const getShippingInfo = useCallback(() => {
        if (!jsonUserData) {
            return {
                governarte: 1,
                shipping_address: "عنوان غير محدد",
                shipping_charges: 50,
                delivered_to: "مستخدم",
                shipping_details: "لا توجد معلومات إضافية",
            };
        }

        // بناء shipping_details بالتفاصيل الكاملة للموظفين
        const detailParts = [];
        if (jsonUserData.mobile)              detailParts.push(`الهاتف: ${jsonUserData.mobile}`);
        if (jsonUserData.governorate?.name)   detailParts.push(`المنطقة: ${jsonUserData.governorate.name}`);
        if (jsonUserData.address)             detailParts.push(`الشارع: ${jsonUserData.address}`);
        if (jsonUserData.city)                detailParts.push(`عمارة: ${jsonUserData.city}`);
        if (jsonUserData.street)              detailParts.push(`دور: ${jsonUserData.street}`);
        if (jsonUserData.building)            detailParts.push(`شقة: ${jsonUserData.building}`);

        return {
            governarte: jsonUserData.governorate?.id || 1,
            shipping_address: getFullAddress(),
            shipping_charges: parseFloat(jsonUserData.governorate?.price || "0"),
            delivered_to: jsonUserData.name || "مستخدم",
            shipping_details: detailParts.join(" | "),
            contact_id: jsonUserData.contact_id,
            user_mobile: jsonUserData.mobile,
        };
    }, [jsonUserData, getFullAddress]);

    // Payment methods configuration with correct API values
    const PAYMENT_METHODS = React.useMemo(
        () => [
            {
                key: "cod",
                apiValue: "cod", // القيمة التي سترسل للـ API
                label: language === "ar" ? "الدفع عند الاستلام" : "Cash on Delivery",
                icon: <TruckOutlined />,
                available: true,
                description: language === "ar" ? "ادفع عند استلام الطلب" : "Pay upon receiving your order",
            },
            {
                key: "instapay",
                apiValue: "instapay", // القيمة التي سترسل للـ API
                label: language === "ar" ? "انستاباي" : "Instapay",
                icon: <CreditCardOutlined />,
                available: true,
                description: language === "ar" ? "الدفع بالبطاقة الائتمانية" : "Credit card payment",
            },
            {
                key: "wallet",
                apiValue: "wallet", // القيمة التي سترسل للـ API
                label: language === "ar" ? "المحفظة الإلكترونية" : "Digital Wallet",
                icon: <WalletOutlined />,
                available: true,
                description: language === "ar" ? "الدفع عبر المحفظة الإلكترونية" : "Pay via digital wallet",
            },
            {
                key: "valu",
                apiValue: "valu", // القيمة التي سترسل للـ API
                label: "Valu",
                icon: <WalletOutlined />,
                available: true,
                description: language === "ar" ? "الدفع عبر valu" : "Pay via valu",
            },
        ],
        []
    );

    const LOADING_DELAY = 500;

    // Helper function to get payment method API value
    const getPaymentMethodApiValue = useCallback(
        (selectedMethod) => {
            const method = PAYMENT_METHODS.find((m) => m.key === selectedMethod);
            return method ? method.apiValue : "cod"; // fallback to cod if not found
        },
        [PAYMENT_METHODS]
    );

    // Memoized functions

    const updateCartData = useCallback(() => {
        const items = getCart();
        const total = getCartTotal();
        const count = getCartItemsCount();

        setCartItems(items);
        setCartTotal(total);
        setCartItemsCount(count);
    }, []);


    const handleClearCart = useCallback(() => {
        try {
            const success = clearCart();
            if (success) {
                updateCartData();
                updateCartCount();
                message.success(language === "ar" ? "تم تفريغ السلة بنجاح!" : "Cart cleared successfully!");
                setIsClearModalOpen(false);
            } else {
                throw new Error("Failed to clear cart");
            }
        } catch (error) {
            message.error(language === "ar" ? "فشل في تفريغ السلة" : "Failed to clear cart");
        }
    }, [updateCartData, updateCartCount]);

    const handleConfirmOrder = useCallback(async () => {
        if (cartItems.length === 0) {
            message.warning(language === "ar" ? "السلة فارغة!" : "Cart is empty!");
            return;
        }

        // التحقق من وجود بيانات المستخدم - توجيه لتسجيل الدخول مع حفظ المسار للعودة
        if (!jsonUserData || !jsonUserData.contact_id) {
            navigate("/Login", {state: {from: location.pathname}});
            return;
        }

        // Check if selected payment method is available
        const selectedMethod = PAYMENT_METHODS.find((method) => method.key === selectedPaymentMethod);
        if (!selectedMethod?.available) {
            message.error(
                language === "ar"
                    ? "طريقة الدفع المختارة غير متاحة حالياً"
                    : "The selected payment method is currently unavailable"
            );
            return;
        }

        // For cash on delivery, show confirmation modal
        if (selectedPaymentMethod === "cod") {
            setIsConfirmModalOpen(true);
            return;
        }

        // For other payment methods, also show confirmation modal
        // يمكنك هنا إضافة منطق مختلف لكل طريقة دفع إذا كنت تريد
        setIsConfirmModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItems.length, selectedPaymentMethod, PAYMENT_METHODS, jsonUserData]);

    const handleConfirmCODOrder = useCallback(async () => {
        try {
            const contactId = jsonUserData?.contact_id;
            const shippingInfo = getShippingInfo();

            console.log("Shipping Info:", shippingInfo); // للتأكد من البيانات

            // Validate order data first
            const validation = validateOrderData(contactId, shippingInfo);
            if (!validation.isValid) {
                message.error(
                    language === "ar"
                        ? `خطأ: ${validation.errors.join(", ")}`
                        : `Error: ${validation.errors.join(", ")}`
                );
                return;
            }

            // Get the correct API value for the selected payment method
            const paymentMethodValue = getPaymentMethodApiValue(selectedPaymentMethod);

            // Prepare order data with correct payment method
            const orderPrep = prepareOrderData(contactId, {
                ...shippingInfo,
                payment_method: paymentMethodValue,
            });

            if (!orderPrep.success) {
                message.error(language === "ar" ? `خطأ: ${orderPrep.error}` : `Error: ${orderPrep.error}`);
                return;
            }

            console.log("Order Data:", orderPrep.data);

            // Create order (first attempt) to check stock from API
            let apiResponse = await createOrder(orderPrep.data).unwrap();

            // Handle stock validation from API
            const productsStock = Array.isArray(apiResponse?.products_stock) ? apiResponse.products_stock : [];

            // If API indicates some products are out of stock, show modal
            if (productsStock.length > 0) {
                const unavailableItems = productsStock.filter(
                    (p) => p?.status === false || parseFloat(p?.stock ?? "0") <= 0
                );
                // eslint-disable-next-line no-unused-vars
                const availableItems = productsStock.filter(
                    (p) => p?.status === true && parseFloat(p?.stock ?? "0") > 0
                );

                if (unavailableItems.length > 0) {
                    // حفظ المنتجات غير المتاحة وعرض Modal للمستخدم
                    console.log("Found out of stock items from API:", unavailableItems);
                    setOutOfStockItems(unavailableItems);
                    setIsConfirmModalOpen(false); // إغلاق Modal التأكيد أولاً
                    setIsOutOfStockModalOpen(true);
                    console.log("Out of stock modal should be open now");
                    return; // إيقاف العملية هنا وانتظار قرار المستخدم
                }
            }

            // If we reach here, either all items were available or API didn't send stock details
            // Clear cart and show success message
            const clearSuccess = clearCart();
            if (clearSuccess) {
                updateCartData();
                updateCartCount();
                setIsConfirmModalOpen(false);
                message.success(language === "ar" ? "تم إنشاء الطلب بنجاح!" : "Order created successfully!");
            }
        } catch (error) {
            const errorMessage = error?.message || error?.data?.message || "خطأ غير معروف";
            message.error(
                language === "ar" ? `فشل في إنشاء الطلب: ${errorMessage}` : `Failed to create order: ${errorMessage}`
            );
        }
    }, [
        createOrder,
        updateCartData,
        updateCartCount,
        selectedPaymentMethod,
        jsonUserData,
        getShippingInfo,
        getPaymentMethodApiValue,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]);

    const handleContinueShopping = useCallback(() => {
        navigate("/products");
    }, [navigate]);

    const handleOpenClearModal = useCallback(() => {
        setIsClearModalOpen(true);
    }, []);

    const handleCloseClearModal = useCallback(() => {
        setIsClearModalOpen(false);
    }, []);

    const handleCloseConfirmModal = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    const handleCloseOutOfStockModal = useCallback(() => {
        setIsOutOfStockModalOpen(false);
        setOutOfStockItems([]);
    }, []);

    const handleProceedWithAvailableItems = useCallback(async () => {
        // إغلاق Modal التحذيري
        setIsOutOfStockModalOpen(false);

        // حذف المنتجات غير المتاحة من السلة
        outOfStockItems.forEach((item) => {
            try {
                removeFromCart(item.product_id);
            } catch {}
        });

        // تحديث بيانات السلة
        updateCartData();
        updateCartCount();

        // المتابعة مع إنشاء الطلب
        await createOrderWithAvailableItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outOfStockItems, updateCartData, updateCartCount]);

    const createOrderWithAvailableItems = useCallback(async () => {
        try {
            const contactId = jsonUserData?.contact_id;
            const shippingInfo = getShippingInfo();

            // التحقق من وجود منتجات متاحة
            const currentCart = getCart();
            const inStockItems = currentCart.filter((item) => parseFloat(item?.stock ?? "0") > 0);

            if (inStockItems.length === 0) {
                message.error(
                    language === "ar"
                        ? "لا توجد منتجات متاحة في السلة لإتمام الطلب"
                        : "No available items in cart to place an order"
                );
                return;
            }

            // Validate order data
            const validation = validateOrderData(contactId, shippingInfo);
            if (!validation.isValid) {
                message.error(
                    language === "ar"
                        ? `خطأ: ${validation.errors.join(", ")}`
                        : `Error: ${validation.errors.join(", ")}`
                );
                return;
            }

            // Get the correct API value for the selected payment method
            const paymentMethodValue = getPaymentMethodApiValue(selectedPaymentMethod);

            // Prepare order data with correct payment method
            const orderPrep = prepareOrderData(contactId, {
                ...shippingInfo,
                payment_method: paymentMethodValue,
            });

            if (!orderPrep.success) {
                message.error(language === "ar" ? `خطأ: ${orderPrep.error}` : `Error: ${orderPrep.error}`);
                return;
            }

            // Create order
            const apiResponse = await createOrder(orderPrep.data).unwrap();

            // Handle stock validation from API
            const productsStock = Array.isArray(apiResponse?.products_stock) ? apiResponse.products_stock : [];

            if (productsStock.length > 0) {
                const unavailableItems = productsStock.filter(
                    (p) => p?.status === false || parseFloat(p?.stock ?? "0") <= 0
                );
                // eslint-disable-next-line no-unused-vars
                const availableItems = productsStock.filter(
                    (p) => p?.status === true && parseFloat(p?.stock ?? "0") > 0
                );

                if (availableItems.length === 0) {
                    const names = unavailableItems.map((p) => p?.name).filter(Boolean);
                    unavailableItems.forEach((prod) => {
                        try {
                            removeFromCart(prod.id);
                        } catch {}
                    });

                    updateCartData();
                    updateCartCount();
                    setIsConfirmModalOpen(false);

                    message.error(
                        language === "ar"
                            ? `عذراً، جميع المنتجات نفدت كميتها وتم حذفها من السلة: ${names.join(", ")}`
                            : `Sorry, all items are out of stock and were removed from the cart: ${names.join(", ")}`
                    );
                    return;
                }

                // Retry creating order with only available product IDs
                const allowedIds = new Set(availableItems.map((p) => parseInt(p.id)));
                const filteredProducts = orderPrep.data.products.filter((p) => allowedIds.has(parseInt(p.id)));

                if (filteredProducts.length !== orderPrep.data.products.length) {
                    const retryData = {
                        ...orderPrep.data,
                        products: filteredProducts,
                    };
                    await createOrder(retryData).unwrap();

                    // eslint-disable-next-line no-unused-vars
                    const removedNames = unavailableItems.map((p) => p?.name).filter(Boolean);

                    // Remove out-of-stock items from localStorage cart
                    unavailableItems.forEach((prod) => {
                        try {
                            removeFromCart(prod.id);
                        } catch {}
                    });

                    // Clear remaining ordered items from cart
                    clearCart();

                    updateCartData();
                    updateCartCount();
                    setIsConfirmModalOpen(false);
                    message.success(
                        language === "ar"
                            ? "تم إنشاء الطلب للمنتجات المتاحة وتم حذف غير المتاحة من السلة"
                            : "Ordered available items. Out-of-stock items were removed from cart."
                    );
                    return;
                }
            }

            // If we reach here, either all items were available or API didn't send stock details
            const clearSuccess = clearCart();
            if (clearSuccess) {
                updateCartData();
                updateCartCount();
                setIsConfirmModalOpen(false);
                message.success(language === "ar" ? "تم إنشاء الطلب بنجاح!" : "Order created successfully!");
            }
        } catch (error) {
            const errorMessage = error?.message || error?.data?.message || "خطأ غير معروف";
            message.error(
                language === "ar" ? `فشل في إنشاء الطلب: ${errorMessage}` : `Failed to create order: ${errorMessage}`
            );
        }
    }, [
        createOrder,
        updateCartData,
        updateCartCount,
        selectedPaymentMethod,
        jsonUserData,
        getShippingInfo,
        getPaymentMethodApiValue,
    ]);

    const handlePaymentMethodChange = useCallback((e) => {
        setSelectedPaymentMethod(e.target.value);
    }, []);

    // Get selected payment method label for display
    const getSelectedPaymentMethodLabel = useCallback(() => {
        const method = PAYMENT_METHODS.find((m) => m.key === selectedPaymentMethod);
        return method ? method.label : "";
    }, [selectedPaymentMethod, PAYMENT_METHODS]);

    // Effects
    useEffect(() => {
        const timer = setTimeout(() => {
            updateCartData();
            setIsLoading(false);
        }, LOADING_DELAY);

        return () => clearTimeout(timer);
    }, [updateCartData]);

    // Render loading state
    if (isLoading) {
        return (
            <div className="container mt-28 h-[70vh] grid place-items-center">
                <Loader2 />
            </div>
        );
    }

    // Render empty cart state
    if (cartItems.length === 0) {
        return (
            <div className="container mt-28 min-h-screen">
                <Helmet>
                    <title>Meat-House El-Fardos</title>
                    <meta
                        name="description"
                        content="راجع منتجاتك المختارة من اللحوم في سلة Meat House وأكمل عملية الشراء بسهولة وأمان."
                    />
                </Helmet>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl sm:text-3xl font-extrabold text-gray-800">
                        {language === "ar" ? "سلة الشراء" : "Shopping Cart"}
                    </h2>
                    <div className="text-sm text-gray-600">(0 {language === "ar" ? "عنصر" : "items"})</div>
                </div>

                <div className="text-center py-20">
                    <div className="text-6xl text-gray-300 mb-4">🛒</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        {language === "ar" ? "السلة فارغة" : "Cart is Empty"}
                    </h3>
                    <p className="text-gray-500 mb-6">
                        {language === "ar"
                            ? "لم تقم بإضافة أي منتجات إلى السلة بعد"
                            : "You haven't added any products to your cart yet"}
                    </p>
                    <Button type="primary" size="large" onClick={handleContinueShopping}>
                        {language === "ar" ? "تسوق الآن" : "Shop Now"}
                    </Button>
                </div>
            </div>
        );
    }

    // Render cart with items
    return (
        <div className="container mt-28 min-h-screen">
            <Helmet>
                <title>Meat-House El-Fardos</title>
                <meta
                    name="description"
                    content="راجع منتجاتك المختارة من اللحوم في سلة Meat House وأكمل عملية الشراء بسهولة وأمان."
                />
            </Helmet>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl sm:text-3xl font-extrabold text-gray-800">
                    {language === "ar" ? "سلة الشراء" : "Shopping Cart"}
                </h2>

                <div className="text-sm text-gray-600">
                    ({cartItemsCount} {language === "ar" ? "عنصر" : "items"})
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Products List */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">{language === "ar" ? "المنتجات" : "Products"}</h3>
                            <Button danger size="small" onClick={handleOpenClearModal} disabled={isCreatingOrder}>
                                {language === "ar" ? "تفريغ السلة" : "Clear Cart"}
                            </Button>
                        </div>

                        {/* مزامنة أسعار كل منتج في السلة مع الـ API فوراً */}
                        {cartItems.map((item) => (
                            <CartItemSyncer
                                key={`sync-${item.product_id}`}
                                productId={item.product_id}
                                count={item.count}
                                onSynced={updateCartData}
                            />
                        ))}
                        <div className="space-y-4">
                            {cartItems.map((item) => (
                                <CartItem key={item.id} item={item} onUpdateCart={updateCartData} />
                            ))}
                        </div>

                    </div>
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {language === "ar" ? "ملخص الطلب" : "Order Summary"}
                        </h3>

                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">{language === "ar" ? "عدد المنتجات:" : "Items:"}</span>
                                <span className="font-medium">{cartItemsCount}</span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    {language === "ar" ? "المجموع الفرعي:" : "Subtotal:"}
                                </span>
                                <span className="font-medium text-mainColor">EGP {parseInt(cartTotal).toFixed(0)}</span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-gray-600">{language === "ar" ? "الشحن:" : "Shipping:"}</span>
                                <span className="font-medium text-mainColor">
                                    {/* EGP {jsonUserData?.governorate?.price} */}
                                    EGP {deliveryPrice}
                                </span>
                            </div>

                            {jsonUserData?.governorate?.time_val && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">
                                        {language === "ar" ? "وقت التوصيل:" : "Delivery time:"}
                                    </span>
                                    <span className="font-medium text-mainColor">
                                        {jsonUserData.governorate.time_val} {t.hours}
                                    </span>
                                </div>
                            )}

                            {deliveryPrice && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">
                                        {language === "ar" ? "المكان:" : "The place:"}
                                    </span>
                                    <span className="font-medium text-mainColor">{userGovernorate}</span>
                                </div>
                            )}

                            <hr className="my-3" />

                            <div className="flex justify-between text-lg font-bold">
                                <span>{language === "ar" ? "الإجمالي:" : "Total:"}</span>
                                <span className="text-mainColor">
                                    EGP{" "}
                                    {/* {(
                                        parseFloat(cartTotal) + parseFloat(jsonUserData?.governorate?.price || "0")
                                    ).toFixed(0)} */}
                                    {(parseFloat(cartTotal) + parseFloat(deliveryPrice)).toFixed(0)}
                                </span>
                            </div>
                        </div>

                        {/* Payment Methods */}
                        <div className="mt-6">
                            <h4 className="text-md font-semibold mb-3">
                                {language === "ar" ? "طريقة الدفع" : "Payment Method"}
                            </h4>
                            <Radio.Group
                                value={selectedPaymentMethod}
                                onChange={handlePaymentMethodChange}
                                className="w-full"
                            >
                                <Space direction="vertical" className="w-full">
                                    {PAYMENT_METHODS.map((method) => (
                                        <Radio
                                            key={method.key}
                                            value={method.key}
                                            disabled={!method.available}
                                            className="w-full"
                                        >
                                            <div className="flex items-center gap-2">
                                                {method.icon}
                                                <div>
                                                    <div className="font-medium">{method.label}</div>
                                                    <div className="text-xs text-gray-500">{method.description}</div>
                                                </div>
                                            </div>
                                        </Radio>
                                    ))}
                                </Space>
                            </Radio.Group>
                        </div>

                        {/* Action Buttons */}
                        <Button
                            type="primary"
                            size="large"
                            className="w-full mt-6"
                            onClick={handleConfirmOrder}
                            loading={isCreatingOrder}
                            disabled={cartItems.length === 0}
                        >
                            {isCreatingOrder
                                ? language === "ar"
                                    ? "جاري الإرسال..."
                                    : "Processing..."
                                : language === "ar"
                                ? "تأكيد الطلب"
                                : "Confirm Order"}
                        </Button>

                        <Button
                            type="default"
                            size="large"
                            className="w-full mt-3"
                            onClick={handleContinueShopping}
                            disabled={isCreatingOrder}
                        >
                            {language === "ar" ? "متابعة التسوق" : "Continue Shopping"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Clear Cart Confirmation Modal */}
            <Modal
                title={language === "ar" ? "تأكيد تفريغ السلة" : "Confirm Clear Cart"}
                open={isClearModalOpen}
                onCancel={handleCloseClearModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseClearModal}>
                        {language === "ar" ? "إلغاء" : "Cancel"}
                    </Button>,
                    <Button key="clear" type="primary" danger onClick={handleClearCart} loading={isCreatingOrder}>
                        {language === "ar" ? "تفريغ السلة" : "Clear Cart"}
                    </Button>,
                ]}
            >
                <p>
                    {language === "ar"
                        ? "هل أنت متأكد من أنك تريد تفريغ السلة؟ سيتم حذف جميع المنتجات."
                        : "Are you sure you want to clear the cart? All products will be removed."}
                </p>
            </Modal>

            {/* Order Confirmation Modal */}
            <Modal
                title={language === "ar" ? "تأكيد الطلب" : "Confirm Order"}
                open={isConfirmModalOpen}
                onCancel={handleCloseConfirmModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseConfirmModal}>
                        {language === "ar" ? "إلغاء" : "Cancel"}
                    </Button>,
                    <Button key="confirm" type="primary" onClick={handleConfirmCODOrder} loading={isCreatingOrder}>
                        {isCreatingOrder
                            ? language === "ar"
                                ? "جاري الإرسال..."
                                : "Processing..."
                            : language === "ar"
                            ? "تأكيد الطلب"
                            : "Confirm Order"}
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    {/* تنبيه مواعيد العمل */}
                    {workHoursData && !workHoursData.is_open_now && (
                        <div style={{
                            background: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: 8,
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            direction: "rtl",
                        }}>
                            <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
                            <div>
                                <p style={{ fontWeight: "bold", color: "#856404", margin: 0, fontFamily: "Cairo, sans-serif" }}>
                                    المتجر مغلق حالياً
                                </p>
                                {workHoursData.message && (
                                    <p style={{ color: "#533f03", margin: "4px 0 0", fontSize: 14, fontFamily: "Cairo, sans-serif" }}>
                                        {workHoursData.message}
                                    </p>
                                )}
                                <p style={{ color: "#856404", margin: "4px 0 0", fontSize: 13, fontFamily: "Cairo, sans-serif" }}>
                                    سيتم تنفيذ طلبك عند بداية ساعات العمل.
                                </p>
                            </div>
                        </div>
                    )}

                    {workHoursData?.is_open_now && workHoursData?.today && (
                        <div style={{
                            background: "#d4edda",
                            border: "1px solid #28a745",
                            borderRadius: 8,
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            direction: "rtl",
                        }}>
                            <span style={{ fontSize: 18 }}>✅</span>
                            <p style={{ margin: 0, color: "#155724", fontSize: 14, fontFamily: "Cairo, sans-serif" }}>
                                المتجر مفتوح الآن — مواعيد اليوم:{" "}
                                <strong>{workHoursData.today.open_time}</strong> إلى{" "}
                                <strong>{workHoursData.today.close_time}</strong>
                            </p>
                        </div>
                    )}

                    <p>
                        {language === "ar"
                            ? "هل أنت متأكد من تأكيد هذا الطلب؟"
                            : "Are you sure you want to confirm this order?"}
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">
                                {language === "ar" ? "طريقة الدفع:" : "Payment Method:"}
                            </span>
                            <span className="font-medium">{getSelectedPaymentMethodLabel()}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">{language === "ar" ? "عدد المنتجات:" : "Items:"}</span>
                            <span className="font-medium">{cartItemsCount}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">{language === "ar" ? "العنوان:" : "Address:"}</span>
                            <span className="font-medium text-sm">{getFullAddress()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">{language === "ar" ? "الإجمالي:" : "Total:"}</span>
                            <span className="font-bold text-mainColor">
                                EGP{" "}
                                {/* {(parseFloat(cartTotal) + parseFloat(jsonUserData?.governorate?.price || "0")).toFixed(
                                    0
                                )} */}
                                {(parseFloat(cartTotal) + parseFloat(deliveryPrice)).toFixed(0)}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Out of Stock Warning Modal */}
            <Modal
                title={language === "ar" ? "منتجات غير متاحة" : "Out of Stock Items"}
                open={isOutOfStockModalOpen}
                onCancel={handleCloseOutOfStockModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseOutOfStockModal}>
                        {language === "ar" ? " تعديل الطلب  " : "Cancel Order"}
                    </Button>,
                    <Button
                        key="proceed"
                        type="primary"
                        onClick={handleProceedWithAvailableItems}
                        loading={isCreatingOrder}
                    >
                        {language === "ar" ? "تأكيد مع المنتجات المتاحة" : "Proceed with Available Items"}
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="text-yellow-500 text-xl">⚠️</div>
                        <p className="text-lg font-medium">
                            {language === "ar"
                                ? "المنتجات التالية غير متاحة حالياً:"
                                : "The following items are currently out of stock:"}
                        </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <ul className="space-y-2">
                            {outOfStockItems.map((item, index) => (
                                <li
                                    key={index}
                                    className="flex justify-between items-center py-2 border-b border-yellow-200 last:border-b-0"
                                >
                                    <span className="font-medium text-gray-800">{item?.item_name || item?.name}</span>
                                    <span className="text-sm text-gray-600">
                                        {language === "ar" ? "غير متاح" : "Out of Stock"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            {language === "ar"
                                ? "هل تريد المتابعة مع المنتجات المتاحة فقط؟ سيتم حذف المنتجات غير المتاحة من السلة."
                                : "Do you want to proceed with available items only? Out-of-stock items will be removed from your cart."}
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CartList;
